/**

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

 * App Initialization - Phase 8 Migration
 * Отвечает за инициализацию основных компонентов приложения
 */


import { initializeCoreSettings as initCoreSettings, clearOldLocalStorageEntries as clearOldLS } from './app-initialization.core.js';
import { initializeDateFilters as initDateFilters } from './app-initialization.filters.js';
import { initializeSearchInput as initSearchInput, handleSearchInput as searchHandleInput, handleSearchSubmit as searchHandleSubmit, debounceSearch as searchDebounce, executeSearch as searchExecute, performFulltextSearch as searchPerformFulltext, renderFulltextResults as searchRenderResults, hasFulltextResults as searchHasFulltextResults } from './app-initialization.search.js';
import { initializeFormSubmit as initFormSubmit, handleFormSubmit as formsHandleSubmit, handleFilterFormSubmit as formsHandleFilterSubmit } from './app-initialization.forms.js';
import { checkPublicModeNotification as checkPublicNotif, showPublicModeNotification as showPublicNotif, getPublicModeNotificationContent as getPublicContent } from './app-initialization.notifications.js';
import { initializeBrowserHistoryHandler } from './browser-history-handler.js';


/**
 * App Initialization Manager
 * Управляет инициализацией всех компонентов приложения
 */
class AppInitializationManager {
    constructor() {
        this.isInitialized = false;
        this.initSteps = new Map();
        this.publicModeEnabled = false;
        
        // Manager initialization logging only in debug mode
    }
    
    /**
     * Основная функция инициализации приложения
     * Мигрировано из main.js document.ready
     */
    async initializeApp() {
        if (this.isInitialized) {
            return true;
        }
        
        // ✅ CRITICAL: Register component status with weight (percentage)
        if(window.ComponentLoadingStatus){
            let appInitCaption = 'Initializing application...';
            if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                try {
                    const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                    const translated = window.TranslationRegistry.translate('loader_stage_initializing_application', lang);
                    if (translated !== 'loader_stage_initializing_application') {
                        appInitCaption = translated;
                    }
                } catch(_) {}
            }
            window.ComponentLoadingStatus.register('appInitialization', 'loading', appInitCaption, 2);
        }
        
        try {
            
            // Step 1: Initialize core settings
            await this.initializeCoreSettings();
            this.markStep('core', true);
            
            // Step 2: Initialize date filters
            await this.initializeDateFilters();
            this.markStep('dateFilters', true);
            
            // Step 3: Initialize search input
            await this.initializeSearchInput();
            this.markStep('searchInput', true);
            
            // Step 4: Initialize form submit handler
            await this.initializeFormSubmit();
            this.markStep('formSubmit', true);
            
            // Step 5: Show notifications if needed
            await this.checkPublicModeNotification();
            this.markStep('notifications', true);
            
            // Step 6: Initialize browser history handler for back/forward navigation
            try {
                initializeBrowserHistoryHandler();
            } catch (error) {
                console.error('[AppInitialization] Failed to initialize browser history handler:', error);
            }
            this.markStep('browserHistory', true);
            
            this.isInitialized = true;
            
            // ✅ CRITICAL: Update component status to 'ready'
            if(window.ComponentLoadingStatus){
                let appInitializedCaption = 'Application initialized';
                if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                    try {
                        const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                        const translated = window.TranslationRegistry.translate('loader_stage_application_initialized', lang);
                        if (translated !== 'loader_stage_application_initialized') {
                            appInitializedCaption = translated;
                        }
                    } catch(_) {}
                }
                window.ComponentLoadingStatus.update('appInitialization', 'ready', appInitializedCaption);
            }
            
            // Fulltext search will be triggered after loadInitialData completes
            // Check is done there to ensure all data is loaded first

            return true;
            
        } catch (error) {
            console.error('[AppInitialization] Failed to initialize app:', error);
            
            // ✅ CRITICAL: Update component status to 'error' on failure
            if(window.ComponentLoadingStatus){
                let caption = 'Application initialization failed';
                if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                  const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                  caption = window.TranslationRegistry.translate('loader_stage_application_init_failed', lang);
                }
                window.ComponentLoadingStatus.update('appInitialization', 'error', caption);
            }
            
            return false;
        }
    }
    
    /**
     * Инициализация основных настроек
     */
    async initializeCoreSettings() { return initCoreSettings(); }
    
    /**
     * Инициализация фильтров дат
     * Мигрировано из main.js initializeDateFilters
     */
    async initializeDateFilters() { return initDateFilters(); }
    
    /**
     * Инициализация обработчика поиска
     * Мигрировано из main.js initializeSearchInput
     */
    async initializeSearchInput() { return initSearchInput(); }
    
    /**
     * Инициализация обработчика отправки форм
     * Мигрировано из main.js initializeFormSubmit
     */
    async initializeFormSubmit() { return initFormSubmit(); }
    
    /**
     * Проверка и показ уведомления о публичном режиме
     * Мигрировано из main.js showPublicModeNotification
     */
    async checkPublicModeNotification() { this.publicModeEnabled = (window.isPublicMode === true || window.PUBLIC_MODE === true || window.location.href.includes('public=true')); if (this.publicModeEnabled) { await showPublicNotif(); } }
    
    /**
     * Показ уведомления о публичном режиме
     */
    async showPublicModeNotification() { return showPublicNotif(); }
    
    /**
     * Получение контента уведомления о публичном режиме
     */
    getPublicModeNotificationContent() { return getPublicContent(); }
    
    /**
     * Обработчик ввода в поле поиска
     */
    handleSearchInput(event) { return searchHandleInput(event); }
    
    /**
     * Обработчик отправки поиска
     */
    handleSearchSubmit(event) { return searchHandleSubmit(event); }
    
    /**
     * Обработчик отправки основной формы
     */
    handleFormSubmit(event) {
        event.preventDefault();
        
        const form = $(event.target);
        if (debugMode === '1') {
                console.log('[AppInitialization] Main form submitted');
            }
        
        // Prefer the visible main input used on the page
        const searchInput = $('#fnd_searchbox, input[name="sq"], input[name="search"]').first();
        const query = (searchInput.val() || '').toString().trim();
        if (!query) {
            // Show error message for empty query
            const $container = $('#nav-full-text');
            const $empty = $container.find('.empty-content');
            if ($empty.length) {
                let errorMsg = 'Please enter a search query';
                if (typeof translate === 'function') {
                    errorMsg = translate('search_empty_query_error');
                } else if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                    errorMsg = window.TranslationRegistry.translate('search_empty_query_error', window.LANG || 'EN');
                }
                $empty.removeClass('d-none').find('.mb-1').text(errorMsg);
            }
            // Ensure Content tab is active to show the error
            try {
                const btn = document.getElementById('nav-full-text-card-tab');
                if (btn && window.bootstrap && typeof window.bootstrap.Tab === 'function') { new window.bootstrap.Tab(btn).show(); }
            } catch(_) { /* noop */ }
            return;
        }

        // Update filter controller search query (URL will be updated in performFulltextSearch)
        if (window.filterController) {
            window.filterController.activeFilters.searchQuery = query;
        }

        // Check if user is authenticated - block fulltext search for guests
        const isAuthenticated = !!(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated);
        if (!isAuthenticated) {
            // Redirect to login with proper next parameter
            const currentUrl = window.location.pathname + (window.location.search || '') + '#fulltext';
            window.location.href = `/login?next=${encodeURIComponent(currentUrl)}`;
            return;
        }

        // Ensure Content tab is active for full-text search
        try {
            const btn = document.getElementById('nav-full-text-card-tab');
            if (btn && window.bootstrap && typeof window.bootstrap.Tab === 'function') { new window.bootstrap.Tab(btn).show(); }
        } catch(_) { /* noop */ }

        // If Axios/searchApi is available, perform backend Whoosh search and render results
        if (window.searchApi && window.axios) {
            this.performFulltextSearch(query);
        } else {
            // Fallback to legacy client-side filter
            this.executeSearch(query);
        }
    }
    
    /**
     * Обработчик отправки форм фильтров
     */
    handleFilterFormSubmit(event) {
        event.preventDefault();
        
        const form = $(event.target);
        if (debugMode === '1') {
                console.log('[AppInitialization] Filter form submitted');
            }
        
        // Trigger filter update
        if (window.updateFilters && typeof window.updateFilters === 'function') {
            window.updateFilters();
        }
    }
    
    /**
     * Debounced search execution
     */
    debounceSearch(query) { return searchDebounce(query); }
    
    /**
     * Execute search
     */
    executeSearch(query) { return searchExecute(query); }

    /**
     * Perform full-text search via backend API and render into #nav-full-text
     */
    async performFulltextSearch(query, page = 1) { return searchPerformFulltext(query, page); }

    /**
     * Проверяет наличие результатов полнотекстового поиска
     */
    hasFulltextResults() { return searchHasFulltextResults(); }

    /**
     * Render full-text results using simple client-side template into #nav-full-text
     */
    renderFulltextResults(data) { return searchRenderResults(data); }
    
    /**
     * Очистка старых записей localStorage
     */
    clearOldLocalStorageEntries() { return clearOldLS(); }
    
    /**
     * Отметка выполнения шага инициализации
     */
    markStep(stepName, completed) {
        this.initSteps.set(stepName, {
            completed,
            timestamp: Date.now()
        });
    }
    
    /**
     * Получение статистики инициализации
     */
    getInitializationStatus() {
        const steps = {};
        for (const [name, data] of this.initSteps) {
            steps[name] = data;
        }
        
        return {
            isInitialized: this.isInitialized,
            publicModeEnabled: this.publicModeEnabled,
            completedSteps: Array.from(this.initSteps.keys()).filter(k => this.initSteps.get(k).completed),
            totalSteps: this.initSteps.size,
            steps
        };
    }
}

// Создаем singleton instance
const appInitializationManager = new AppInitializationManager();


// Global exports для backward compatibility
window.appInitializationManager = appInitializationManager;
window.initializeApp = appInitializationManager.initializeApp.bind(appInitializationManager);
window.initializeDateFilters = appInitializationManager.initializeDateFilters.bind(appInitializationManager);
window.initializeSearchInput = appInitializationManager.initializeSearchInput.bind(appInitializationManager);
window.initializeFormSubmit = appInitializationManager.initializeFormSubmit.bind(appInitializationManager);
window.showPublicModeNotification = appInitializationManager.showPublicModeNotification.bind(appInitializationManager);

// ES Module exports
export { appInitializationManager };
export const { 
    initializeApp, 
    initializeDateFilters, 
    initializeSearchInput, 
    initializeFormSubmit, 
    showPublicModeNotification 
} = appInitializationManager; 